import type { Notification, NotificationInput, NotificationType } from '@contember/bindx'
import { useDismissNotification, useNotifications, useShowNotification } from '@contember/bindx-react'
import { AlertCircle, CheckCircle2, Info, TriangleAlert, X } from 'lucide-react'
import { type ReactNode, useCallback, useState } from 'react'

const nativeMessageTranslations: Record<string, string> = {
	'Changes saved successfully': 'Saved.',
	'Failed to save changes': 'Saving failed.',
	'Please fix validation errors before saving': 'Fix the highlighted errors before saving.',
}

const toastStyles: Record<NotificationType, { frame: string; icon: ReactNode }> = {
	success: {
		frame: 'border-success/30 bg-success-bg text-success',
		icon: <CheckCircle2 aria-hidden className="mt-0.5 size-5 shrink-0" />,
	},
	error: {
		frame: 'border-danger/30 bg-danger-bg text-danger',
		icon: <AlertCircle aria-hidden className="mt-0.5 size-5 shrink-0" />,
	},
	warning: {
		frame: 'border-warning/30 bg-warning-bg text-warning',
		icon: <TriangleAlert aria-hidden className="mt-0.5 size-5 shrink-0" />,
	},
	info: {
		frame: 'border-light-blue/40 bg-white text-blue-primary',
		icon: <Info aria-hidden className="mt-0.5 size-5 shrink-0" />,
	},
}

type ToastOptions = Partial<Omit<NotificationInput, 'message' | 'type'>>

export function useAdminToast() {
	const showNotification = useShowNotification()

	const show = useCallback(
		(input: NotificationInput) => showNotification(input),
		[showNotification],
	)

	const success = useCallback(
		(message: string, options?: ToastOptions) =>
			showNotification({
				dismissAfter: 6_000,
				source: 'client',
				...options,
				message,
				type: 'success',
			}),
		[showNotification],
	)

	const error = useCallback(
		(message: string, options?: ToastOptions) =>
			showNotification({
				dismissAfter: 60_000,
				source: 'client',
				...options,
				message,
				type: 'error',
			}),
		[showNotification],
	)

	const warning = useCallback(
		(message: string, options?: ToastOptions) =>
			showNotification({
				dismissAfter: 15_000,
				source: 'client',
				...options,
				message,
				type: 'warning',
			}),
		[showNotification],
	)

	const info = useCallback(
		(message: string, options?: ToastOptions) =>
			showNotification({
				dismissAfter: 6_000,
				source: 'client',
				...options,
				message,
				type: 'info',
			}),
		[showNotification],
	)

	return { show, success, error, warning, info }
}

export function AdminToastContainer() {
	const notifications = useNotifications()
	const dismiss = useDismissNotification()

	if (notifications.length === 0) return null

	return (
		<div
			aria-label="Notification"
			aria-live="polite"
			className="pointer-events-none fixed bottom-4 right-4 z-50 flex w-[calc(100vw-2rem)] max-w-sm flex-col gap-2"
		>
			{notifications.map(notification => <AdminToastItem key={notification.id} notification={notification} onDismiss={dismiss} />)}
		</div>
	)
}

function AdminToastItem({ notification, onDismiss }: { notification: Notification; onDismiss: (id: string) => void }) {
	const [showDetails, setShowDetails] = useState(false)
	const style = toastStyles[notification.type]
	const message = nativeMessageTranslations[notification.message] ?? notification.message
	const hasDetails = notification.details && notification.details.length > 0

	return (
		<div
			className={`pointer-events-auto rounded-md border px-4 py-3 shadow-lg ${style.frame}`}
			role="alert"
		>
			<div className="flex items-start gap-3">
				{style.icon}
				<div className="min-w-0 flex-1">
					<p className="text-sm font-medium text-foreground">{message}</p>

					{hasDetails
						? (
							<>
								<button
									className="mt-1 text-xs text-gray underline hover:text-text-dark"
									onClick={() => setShowDetails(previous => !previous)}
									type="button"
								>
									{showDetails ? 'Hide details' : 'Zobrazit podrobnosti'}
								</button>
								{showDetails
									? (
										<ul className="mt-2 space-y-1 text-xs text-text">
											{notification.details?.map((detail, index) => (
												<li key={index}>
													{detail.entityType ? <span className="font-medium">{detail.entityType}</span> : null}
													{detail.fieldName ? <span className="font-medium">.{detail.fieldName}</span> : null}
													{detail.entityType || detail.fieldName ? ': ' : null}
													{detail.message}
												</li>
											))}
										</ul>
									)
									: null}
							</>
						)
						: null}

					{notification.technicalDetail ? <p className="mt-1 truncate font-mono text-xs text-gray">{notification.technicalDetail}</p> : null}
				</div>
				<button
					aria-label="Close notification"
					className="shrink-0 rounded-sm p-1 text-gray transition-colors hover:text-text-dark"
					onClick={() => onDismiss(notification.id)}
					type="button"
				>
					<X aria-hidden className="size-4" />
				</button>
			</div>
		</div>
	)
}
